import type { FormLockResponse } from '@common/types/formLocks';
import type { RetainerContactAgreementResponse } from '@common/types/retainerAgreements';
import { useCallback, useMemo, useState } from 'react';
import { getRetainerDetailsUsingProspectId } from '@/services/ClientSideTemplateFormService';

export type RetainerCheckResult =
    | { requiresSignature: false }
    | { requiresSignature: true; showLegalDocument: true };

interface UseRetainerFlowOptions {
    prospectId: string | null;
    contactId: string;
}

interface UseRetainerFlowReturn {
    retainerResponse: RetainerContactAgreementResponse[];
    showLegalDocument: boolean;
    openLegalDocument: () => void;
    closeLegalDocument: () => void;
    clientName: string | undefined;
    areAllRetainersSigned: boolean;
    fetchRetainerDetails: (
        forProspectId?: string | null,
    ) => Promise<RetainerContactAgreementResponse[] | undefined>;

    /**
     * Check if a retainer signature is required and update state accordingly.
     * Call this after acquiring a lock when the lock info indicates a retainer exists.
     *
     * @param lockInfo - The lock info from acquireLock result
     * @param forContactId - The contact ID to check signature status for
     * @returns Result indicating if signature is required
     */
    checkRetainerRequired: (
        lockInfo: FormLockResponse | null,
        forContactId: string,
    ) => Promise<RetainerCheckResult>;

    /**
     * Check if a specific contact has signed their retainer.
     */
    isRetainerSignedForContact: (forContactId: string) => boolean;
}

/**
 * Hook to manage the retainer signing flow for intake forms.
 *
 * Handles:
 * - Fetching retainer details for a prospect
 * - Checking if retainer signature is required
 * - Managing the legal document modal visibility
 * - Computing derived values like client name and signature status
 */
export function useRetainerFlow({
    prospectId,
    contactId,
}: UseRetainerFlowOptions): UseRetainerFlowReturn {
    const [retainerResponse, setRetainerResponse] = useState<RetainerContactAgreementResponse[]>(
        [],
    );
    const [showLegalDocument, setShowLegalDocument] = useState(false);

    const openLegalDocument = useCallback(() => setShowLegalDocument(true), []);
    const closeLegalDocument = useCallback(() => setShowLegalDocument(false), []);

    // Compute client name from retainer response for pre-filling signature
    const clientName = useMemo(() => {
        const matchedContact = retainerResponse.find((item) => item.contactId === contactId);
        if (!matchedContact) return undefined;
        return matchedContact.middleName
            ? `${matchedContact.firstName} ${matchedContact.middleName} ${matchedContact.lastName}`
            : `${matchedContact.firstName} ${matchedContact.lastName}`;
    }, [retainerResponse, contactId]);

    // Check if all retainers have been signed
    const areAllRetainersSigned = useMemo(() => {
        if (retainerResponse.length === 0) return true;
        return retainerResponse.every((item) => item.isRetainerSigned === true);
    }, [retainerResponse]);

    const fetchRetainerDetails = useCallback(
        async (
            forProspectId?: string | null,
        ): Promise<RetainerContactAgreementResponse[] | undefined> => {
            const id = forProspectId || prospectId;
            if (!id) return undefined;

            try {
                const resp = await getRetainerDetailsUsingProspectId(id);
                setRetainerResponse(resp?.data ?? []);
                return resp?.data;
            } catch (error) {
                console.error('Fetch retainer details failed:', error);
                return undefined;
            }
        },
        [prospectId],
    );

    const isRetainerSignedForContact = useCallback(
        (forContactId: string): boolean => {
            return retainerResponse.some(
                (item) => item.contactId === forContactId && item.isRetainerSigned,
            );
        },
        [retainerResponse],
    );

    const checkRetainerRequired = useCallback(
        async (
            lockInfo: FormLockResponse | null,
            forContactId: string,
        ): Promise<RetainerCheckResult> => {
            // No retainer configured for this form
            if (!lockInfo?.globalRetainerFileId) {
                return { requiresSignature: false };
            }

            // Fetch latest retainer status. Prefer the lock's prospectId so this
            // works on post-intake, where the URL often has no prospectId yet.
            const retainerData = await fetchRetainerDetails(lockInfo.prospectId);
            // Check if this contact has already signed
            const isSigned = retainerData?.some(
                (item) => item.contactId === forContactId && item.isRetainerSigned,
            );
            if (isSigned) {
                return { requiresSignature: false };
            }

            // Retainer not signed - show the legal document modal
            setShowLegalDocument(true);
            return { requiresSignature: true, showLegalDocument: true };
        },
        [fetchRetainerDetails],
    );

    return {
        retainerResponse,
        showLegalDocument,
        openLegalDocument,
        closeLegalDocument,
        clientName,
        areAllRetainersSigned,
        fetchRetainerDetails,
        checkRetainerRequired,
        isRetainerSignedForContact,
    };
}
